import { defineMessages } from "@formatjs/intl";
import cs from "classnames";
import React from "react";
import { HiSortAscending, HiSortDescending } from "react-icons/hi";
import { MdOutlineSearch } from "react-icons/md";
import { useIntl } from "react-intl";

import {
	ButtonGroup,
	ButtonGroupItem,
	ButtonGroupVariant
} from "src/common/components/ButtonGroup";
import { SortDirection } from "src/common/components/DataTable";
import { MobileColumnProps } from "src/common/components/MobileTable/types";
import { useFlatColumns } from "src/common/components/MobileTable/useFlatColumns";
import { Select } from "src/common/components/Select";

import "./MobileTableToolbar.css";

const messages = defineMessages({
	sortBy: {
		id: "mobile_table.sort_by",
		defaultMessage: "Rendezés alapja"
	},
	asc: {
		id: "mobile_table.asc",
		defaultMessage: "Növekvő"
	},
	desc: {
		id: "mobile_table.desc",
		defaultMessage: "Csökkenő"
	}
});

export const MobileTableToolbar: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => <div className="mobile-table-toolbar">{children}</div>;

export const MobileTableTitle: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => <div className="mobile-table-title">{children}</div>;

interface MobileTableSearchProps {
	placeholder: string;
	query: string;
	setQuery(query: string): unknown;
}

export const MobileTableSearch: React.FunctionComponent<
	MobileTableSearchProps
> = ({ placeholder, query, setQuery }) => {
	const [isFocused, setFocused] = React.useState(false);

	const onFocus = React.useCallback(() => {
		setFocused(true);
	}, []);

	const onBlur = React.useCallback(() => {
		setFocused(false);
	}, []);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setQuery(event.target.value);
		},
		[setQuery]
	);

	return (
		<div
			className={cs("mobile-table-search", {
				"mobile-table-search--focused": isFocused
			})}
		>
			<MdOutlineSearch />
			<input
				type="text"
				placeholder={placeholder}
				value={query}
				onFocus={onFocus}
				onBlur={onBlur}
				onChange={onChange}
			/>
		</div>
	);
};

interface MobileTableSortProps {
	columns: MobileColumnProps;
	sortKey: string;
	setSortKey(value: string): unknown;
	sortDirection: SortDirection;
	setSortDirection(value: SortDirection): unknown;
}

export const MobileTableSort: React.FunctionComponent<MobileTableSortProps> = ({
	columns,
	sortKey,
	setSortKey,
	sortDirection,
	setSortDirection
}) => {
	const { formatMessage } = useIntl();

	const flatColumns = useFlatColumns(columns);
	const sortKeyOptions = React.useMemo(
		() => flatColumns.map(({ label, key }) => ({ label, value: key })),
		[flatColumns]
	);

	const onChangeSortKey = React.useCallback(
		(value: string | undefined) => {
			if (value !== undefined) {
				setSortKey(value);
			}
		},
		[setSortKey]
	);

	const onChangeSortDirection = React.useCallback(
		(value: string | undefined) => {
			if (value !== undefined) {
				setSortDirection(value as SortDirection);
			}
		},
		[setSortDirection]
	);

	return (
		<div className="mobile-table-sort">
			<div className="mobile-table-sort__key">
				<Select
					placeholder={formatMessage(messages.sortBy)}
					canClear={false}
					options={sortKeyOptions}
					value={sortKey}
					onChangeValue={onChangeSortKey}
				/>
			</div>
			<div className="mobile-table-sort__direction">
				<ButtonGroup variant={ButtonGroupVariant.Square}>
					<ButtonGroupItem
						title={formatMessage(messages.asc)}
						checked={sortDirection === "asc"}
						value="asc"
						onChangeValue={onChangeSortDirection}
					>
						<HiSortAscending />
					</ButtonGroupItem>
					<ButtonGroupItem
						title={formatMessage(messages.desc)}
						checked={sortDirection === "desc"}
						value="desc"
						onChangeValue={onChangeSortDirection}
					>
						<HiSortDescending />
					</ButtonGroupItem>
				</ButtonGroup>
			</div>
		</div>
	);
};

export const MobileTableWrapper: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => <div className="mobile-table-wrapper">{children}</div>;

export const EmptyMobileTableMessage: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => (
	<div className="empty--mobile-table-message">{children}</div>
);
